import { Modal, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { PersonalbedarfMonat } from '@/api/types';
import { MONAT_KURZ } from './utils';

interface Props {
  opened: boolean;
  onClose: () => void;
  monatswerte: ReadonlyArray<PersonalbedarfMonat>;
  foerderjahr: number;
}

/**
 * Der Personalbedarf eines Jahres, Zeile für Zeile — Nachbau des Reiters
 * "2. Personalbedarf" aus dem Excel-LFT-Tool.
 *
 * Der Reiter zeigt sonst nur das Jahresmaximum und eine Monatszeile. Das
 * genügt, um zu sehen, WIE VIEL Personal gebraucht wird, aber nicht, um zu
 * verstehen, WORAUS sich das ergibt: Aus wie vielen Netto-Fachkraftstunden,
 * wie viel Ausfall- und Leitungszeit, wie viel Integration, wie viel
 * freiwilligem Aufschlag. Genau diese Aufschlüsselung prüft ein Träger
 * gegen seine eigene Rechnung — und genau so steht sie im Excel.
 *
 * Die Zahlen kommen vollständig aus der Personalbedarfs-Antwort des
 * Backends. Hier wird nichts nachgerechnet außer dem Jahresmittel: Eine
 * zweite Rechnung könnte von der ersten abweichen und sähe dabei aus wie
 * deren Erklärung.
 */
export function PersonalbedarfJahresuebersicht({
  opened,
  onClose,
  monatswerte,
  foerderjahr,
}: Props) {
  const { t } = useTranslation(['belegung', 'common']);

  const sortiert = [...monatswerte].sort((a, b) => a.monat - b.monat);

  /** Jahresmittel einer Zeile — die Spalte "Ø Woche" der Excel-Vorlage. */
  const mittel = (werte: number[]) =>
    werte.length === 0 ? 0 : werte.reduce((s, w) => s + w, 0) / werte.length;

  const spalte = (feld: (m: PersonalbedarfMonat) => number) => sortiert.map(feld);

  const netto = spalte((m) => m.netto_h_woche);
  const ausfall = spalte((m) => m.ausfall_h_woche);
  const leitung = spalte((m) => m.leitung_h_woche);
  const integration = spalte((m) => m.integration_h_woche);
  const mpz = spalte((m) => m.mpz_h_woche);
  const gesamt = spalte((m) => m.h_woche);
  // Der gesetzliche Mindestbedarf: alles außer dem freiwilligen Aufschlag.
  //
  // Aus den vier Zeilen darüber gebildet, nicht als `gesamt - mpz`: Die
  // Zahlen stehen sichtbar untereinander, und wer sie addiert, muss auf
  // genau diese Zwischensumme kommen.
  const mindest = netto.map((wert, i) => wert + ausfall[i] + leitung[i] + integration[i]);

  /**
   * Der Anteil einer Zeile am Netto-Bedarf, aus den Zahlen selbst
   * abgeleitet — 22 %, 20 %, der eingestellte mpz-Satz.
   *
   * Bewusst nicht als Konstante hinterlegt: Die Sätze stehen in der Engine
   * bzw. am Plan. Eine zweite Angabe im Frontend würde irgendwann von der
   * Rechnung abweichen und dann das Falsche behaupten.
   */
  const anteil = (werte: number[]): string | null => {
    const basis = mittel(netto);
    if (basis <= 0) return null;
    const quote = mittel(werte) / basis;
    if (quote <= 0) return null;
    return `${(quote * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
  };

  const fmt = (n: number) =>
    n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const zeilen: {
    key: string;
    label: string;
    satz: string | null;
    werte: number[];
    betont?: boolean;
    summe?: boolean;
  }[] = [
    {
      key: 'netto',
      label: t('belegung:jahresuebersicht.netto'),
      satz: null,
      werte: netto,
    },
    {
      key: 'ausfall',
      label: t('belegung:jahresuebersicht.ausfall'),
      satz: anteil(ausfall),
      werte: ausfall,
    },
    {
      key: 'leitung',
      label: t('belegung:jahresuebersicht.leitung'),
      satz: anteil(leitung),
      werte: leitung,
    },
    {
      key: 'integration',
      label: t('belegung:jahresuebersicht.integration'),
      satz: null,
      werte: integration,
    },
    {
      key: 'mindest',
      label: t('belegung:jahresuebersicht.mindestbedarf'),
      satz: null,
      werte: mindest,
      summe: true,
    },
    {
      key: 'mpz',
      label: t('belegung:jahresuebersicht.mpz'),
      satz: anteil(mpz),
      werte: mpz,
    },
    {
      key: 'gesamt',
      label: t('belegung:jahresuebersicht.gesamt'),
      satz: null,
      werte: gesamt,
      betont: true,
      summe: true,
    },
  ];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('belegung:jahresuebersicht.titel', { jahr: foerderjahr })}
      size="90rem"
      centered
    >
      <Stack gap="sm" data-testid="personalbedarf-jahresuebersicht">
        <Text size="xs" c="dimmed">
          {t('belegung:jahresuebersicht.hinweis')}
        </Text>
        <Table.ScrollContainer minWidth={980}>
          <Table withTableBorder withColumnBorders striped verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: 300 }} />
                <Table.Th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {t('belegung:jahresuebersicht.oWoche')}
                </Table.Th>
                {sortiert.map((m) => (
                  <Table.Th key={m.monat} style={{ textAlign: 'right' }}>
                    {MONAT_KURZ[m.monat - 1]}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {zeilen.map((zeile) => (
                <Table.Tr
                  key={zeile.key}
                  data-testid={`jahresuebersicht-${zeile.key}`}
                  style={
                    zeile.betont
                      ? { backgroundColor: 'var(--mantine-color-gray-1)' }
                      : undefined
                  }
                >
                  <Table.Td style={{ fontWeight: zeile.summe ? 700 : 400 }}>
                    {zeile.label}
                    {zeile.satz ? (
                      <Text component="span" size="xs" c="dimmed">
                        {' · '}
                        {zeile.satz}
                      </Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td
                    style={{
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      fontWeight: zeile.summe ? 700 : 400,
                    }}
                  >
                    {fmt(mittel(zeile.werte))}
                  </Table.Td>
                  {zeile.werte.map((wert, i) => (
                    <Table.Td
                      key={sortiert[i].monat}
                      style={{
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        fontWeight: zeile.summe ? 700 : 400,
                      }}
                    >
                      {fmt(wert)}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    </Modal>
  );
}
